import React from 'react';
import { Rect, SchematicShell } from './SchematicPrimitives';
import { ceilingLightProportions } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { isHungCeilingLight } from '../../../utils/lightingClassification';
import { HungLightSchematic } from './HungLightSchematic';

/**
 * Elevation schematic for `ceiling-light` — the flush-mount dome, or, for a
 * fixture that HANGS, the stem-and-body drawing in `HungLightSchematic`.
 *
 * The registry is keyed by type and a pendant's type is `ceiling-light`, so
 * the subtype branch lives here rather than as a second registration — the
 * same shape `StorageSchematic` uses for its mount classifier. The predicate
 * is the shared one (`isHungCeilingLight`), so what gets DRAWN as a pendant
 * and what gets ADMITTED to an island's elevation cannot drift apart.
 *
 * Matches `ceilingLightRenderer` in lightingRenderers.js; every number
 * comes from `CEILING_LIGHT` / `ceilingLightProportions` in
 * proceduralSpecs.js. The fixture mounts face-down on the ceiling, so every
 * elevation strip (front, left, right) shows the same SIDE profile: the
 * ceiling plane at the top of the bbox, the metal rim band flush with it,
 * and the dome (half-ellipse) hanging down into the room. Radially
 * symmetric, so all three views are the same drawing over their own
 * horizontal extent (width for the front strip, depth for the sides).
 */
export function CeilingLightSchematic(props) {
  if (isHungCeilingLight(props.obj)) return <HungLightSchematic {...props} />;
  const {
    widthMeters: width,
    heightMeters: height,
    depthMeters: depth,
    view = 'front',
    strokeColor,
    strokeWidth,
  } = props;
  const horizontal = view === 'left' || view === 'right' ? depth : width;
  const { rimR, rimH, domeDrop } = ceilingLightProportions({
    width,
    height,
    depth,
  });
  const cx = horizontal / 2;
  return (
    <SchematicShell
      width={horizontal}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Rim band against the ceiling (y = 0). */}
      <Rect
        x={cx - rimR}
        y={0}
        width={rimR * 2}
        height={rimH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Dome: lower half-ellipse from rim edge to rim edge. */}
      <path
        d={`M ${cx - rimR} ${rimH} A ${rimR} ${domeDrop} 0 0 0 ${cx + rimR} ${rimH}`}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}

CeilingLightSchematic.supportsSideViews = true;
// A ceiling fixture is radially symmetric — the same reason it renders side
// views. `supportsBackView` exists so a schematic never INVENTS a face it
// cannot know (a run's back panel drawn as its doors); a dome and a pendant
// have no face at all, so their back IS their front. Without this, an island's
// far-side elevation fell to the silhouette and drew each pendant as a bare
// 78" rectangle, cord and all.
CeilingLightSchematic.supportsBackView = true;
